import { CSSVariablesResolver } from '@mantine/core';
import merge from 'lodash/merge';

import { ColorsProps } from '../../common/themes/theme';

type DeepObject = { [key: string]: DeepObject | string | number | boolean | null };

const camelToDash = (str: string): string => str.replace(/([A-Z])/g, g => `-${g[0].toLowerCase()}`);

const isValidUrl = (str: string): boolean => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

const formatCssValue = (value: unknown): string => {
  if (typeof value !== 'string') return String(value);

  if (isValidUrl(value)) {
    return `url(${value.trim()})`;
  }

  return value;
};

const createVariables = (obj: DeepObject, prefix: string[] = ['mantine', 'other']): Record<string, string> => {
  const result: Record<string, string> = {};

  const flatten = (current: DeepObject, currentPrefix: string[]): void => {
    for (const [key, value] of Object.entries(current)) {
      const newPrefix = [...currentPrefix, camelToDash(key)];
      const varName = `--${newPrefix.join('-')}`;

      if (value && typeof value === 'object') {
        flatten(value, newPrefix);
      } else if (value !== undefined) {
        result[varName] = formatCssValue(value);
      }
    }
  };

  flatten(obj, prefix);
  return result;
};

export const getColorVariables = (colors: ColorsProps, includeDeleted = true): Record<string, string> => {
  const { content, ...restOfColors } = colors;
  const { additionalColors = [], deletedAdditionalColors = [], ...restOfContent } = content;

  const baseColors = merge({}, restOfColors, {
    content: restOfContent
  });

  return createVariables(
    merge({}, baseColors, ...additionalColors, ...(includeDeleted ? deletedAdditionalColors : [])),
    ['mantine', 'other', 'colors']
  );
};

export const resolver: CSSVariablesResolver = theme => {
  const { colors, ...otherWithoutColors } = theme.other;

  const variables = createVariables(otherWithoutColors);

  const colorVariables = getColorVariables(colors);

  // Also include default Mantine colors (white, black, grays, etc.)
  const mantineColorVariables: Record<string, string> = {};

  // Add theme colors from DEFAULT_THEME
  if (theme.colors) {
    for (const [colorName, colorArray] of Object.entries(theme.colors)) {
      if (Array.isArray(colorArray)) {
        colorArray.forEach((color, index) => {
          mantineColorVariables[`--mantine-color-${colorName}-${index}`] = color;
        });
      }
    }
  }

  // Add white and black if they exist
  if (theme.white) {
    mantineColorVariables['--mantine-color-white'] = theme.white;
  }
  if (theme.black) {
    mantineColorVariables['--mantine-color-black'] = theme.black;
  }

  return {
    variables: merge({}, mantineColorVariables, variables, colorVariables),
    light: {},
    dark: {}
  };
};
